import React, { useState, useEffect } from 'react';
import { Assistant, ASSISTANT_CATEGORIES, AssistantTemplate, AssistantToolConfig } from '../types/assistant';
import { assistantTemplates } from '../data/assistant-templates';
import { aiProviders } from '../data/ai-providers';
import { User, Tenant } from '../types/user';
import { assistantService } from '../services/assistant-service';
import { generateAssistantWithAI } from '../services/assistant-generator-service';
import { useToast } from '../hooks/use-toast';
import { AssistantKnowledgeBase } from './AssistantKnowledgeBase';
import { AssistantToolsConfig } from './AssistantToolsConfig';
import { X, Settings, Wrench, BookOpen, Sparkles, Trash2, Loader2, Wand2, ArrowLeft } from 'lucide-react';

interface AssistantEditorModalProps {
  isOpen: boolean;
  user: User;
  tenant: Tenant;
  assistant?: Assistant; // Se fornecido, está editando
  template?: AssistantTemplate | null; // Se fornecido, pre-preenche com template
  onSave: (assistant: Assistant) => void;
  onClose: () => void;
  onDelete?: (assistantId: string) => void;
}

type TabType = 'config' | 'tools' | 'knowledge';
type CreationMode = 'templates' | 'ai' | 'manual';

export function AssistantEditorModal({ 
  isOpen, 
  user, 
  tenant, 
  assistant, 
  template, 
  onSave, 
  onClose, 
  onDelete 
}: AssistantEditorModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    instructions: '',
    avatar: '🤖',
    aiProvider: 'openai',
    modelId: 'gpt-4o',
    isPublic: false,
    category: 'productivity',
    tags: [] as string[],
    tools: [] as AssistantToolConfig[]
  });
  const [loading, setLoading] = useState(false);
  const [creationMode, setCreationMode] = useState<CreationMode>('templates');
  const [tagInput, setTagInput] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('config');
  
  // Estados para criação com IA
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  
  const toast = useToast();

  // Filtrar provedores disponíveis para o tenant
  const availableProviders = aiProviders.filter(provider => 
    tenant.settings?.enabledAIs?.includes(provider.id) ?? true
  );

  useEffect(() => {
    if (!isOpen) return;
    
    if (template) {
      const suggestedProvider = availableProviders.find(p => p.id === template.suggestedProvider);
      const provider = suggestedProvider || availableProviders[0];
      const modelExists = provider?.models.some(m => m.id === template.suggestedModel);
      const modelId = modelExists ? template.suggestedModel : provider?.models[0]?.id || 'gpt-4o';
      
      setFormData({
        name: template.name,
        description: template.description,
        instructions: template.instructions,
        avatar: template.avatar,
        aiProvider: provider?.id || 'openai',
        modelId: modelId,
        isPublic: false,
        category: template.category,
        tags: template.tags || [],
        tools: []
      });
      setCreationMode('manual');
    } else if (assistant) {
      setFormData({
        name: assistant.name,
        description: assistant.description,
        instructions: assistant.instructions,
        avatar: assistant.avatar || '🤖',
        aiProvider: assistant.aiProvider,
        modelId: assistant.modelId,
        isPublic: assistant.isPublic,
        category: assistant.category || 'productivity',
        tags: assistant.tags || [],
        tools: assistant.tools || []
      });
      setCreationMode('manual');
    } else {
      // Reset para criação
      setFormData({
        name: '',
        description: '',
        instructions: '',
        avatar: '🤖',
        aiProvider: availableProviders[0]?.id || 'openai',
        modelId: availableProviders[0]?.models[0]?.id || 'gpt-4o',
        isPublic: false,
        category: 'productivity',
        tags: [],
        tools: []
      });
      setCreationMode('templates');
      setAiPrompt('');
    }
    setActiveTab('config');
  }, [isOpen, assistant?.id, template?.id]);

  const handleTemplateSelect = (tmpl: AssistantTemplate) => {
    const suggestedProvider = availableProviders.find(p => p.id === tmpl.suggestedProvider);
    const provider = suggestedProvider || availableProviders[0];
    const modelExists = provider?.models.some(m => m.id === tmpl.suggestedModel);
    const modelId = modelExists ? tmpl.suggestedModel : provider?.models[0]?.id || 'gpt-4o';
    
    setFormData({
      name: tmpl.name,
      description: tmpl.description,
      instructions: tmpl.instructions,
      avatar: tmpl.avatar,
      aiProvider: provider?.id || 'openai',
      modelId: modelId,
      isPublic: false,
      category: tmpl.category,
      tags: [...tmpl.tags],
      tools: []
    });
    setCreationMode('manual');
  };

  // Função para gerar assistente com IA
  const handleGenerateWithAI = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Descreva o que o assistente deve fazer');
      return;
    }

    setIsGenerating(true);
    try {
      const generated = await generateAssistantWithAI(aiPrompt);
      
      setFormData(prev => ({
        ...prev,
        name: generated.name,
        description: generated.description,
        instructions: generated.instructions,
        avatar: generated.avatar,
        category: generated.category,
        tags: generated.tags
      }));
      
      setCreationMode('manual');
      toast.success('Agente gerado! Revise e ajuste se necessário.');
    } catch (error) {
      console.error('Erro ao gerar assistente:', error);
      toast.error('Erro ao gerar assistente. Tente novamente.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleProviderChange = (providerId: string) => {
    const provider = availableProviders.find(p => p.id === providerId);
    if (provider && provider.models.length > 0) {
      setFormData(prev => ({
        ...prev,
        aiProvider: providerId,
        modelId: provider.models[0].id
      }));
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.description.trim() || !formData.instructions.trim()) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setLoading(true);
    try {
      let savedAssistant: Assistant;
      
      if (assistant) {
        savedAssistant = await assistantService.updateAssistant(assistant.id, {
          ...formData,
          updatedAt: new Date()
        });
      } else {
        savedAssistant = await assistantService.createAssistant({
          ...formData,
          createdBy: user.id,
          tenantId: tenant.id
        });
      }
      
      toast.success('Agente salvo com sucesso!');
      onSave(savedAssistant);
      onClose();
    } catch (error) {
      console.error('Erro ao salvar agente:', error);
      toast.error('Erro ao salvar agente');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!assistant) return;

    setDeleting(true);
    try {
      await assistantService.deleteAssistant(assistant.id);
      toast.success('Agente excluído com sucesso');
      setShowDeleteModal(false);
      if (onDelete) onDelete(assistant.id);
      onClose();
    } catch (error) {
      console.error('Erro ao excluir agente:', error);
      toast.error('Erro ao excluir agente');
    } finally {
      setDeleting(false);
    }
  };

  const currentProvider = availableProviders.find(p => p.id === formData.aiProvider);

  if (!isOpen) return null;

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'config', label: 'Configurações', icon: <Settings className="w-4 h-4" /> },
    { id: 'tools', label: 'Ferramentas', icon: <Wrench className="w-4 h-4" /> },
    { id: 'knowledge', label: 'Conhecimento', icon: <BookOpen className="w-4 h-4" /> },
  ];

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-[80] transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-4 md:inset-10 lg:inset-16 bg-white rounded-2xl z-[90] flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-gray-50 to-white">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {assistant ? 'Editar Agente' : 'Criar Agente'}
            </h2>
            {formData.name && (
              <p className="text-sm text-gray-500">{formData.name}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tela de Criação com IA */}
        {creationMode === 'ai' && !assistant ? (
          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-2xl mx-auto">
              {/* Botão Voltar */}
              <button
                onClick={() => setCreationMode('templates')}
                className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar
              </button>

              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Wand2 className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Criar com IA</h3>
                <p className="text-gray-600">
                  Descreva o que você precisa e a IA vai criar o agente perfeito para você
                </p>
              </div>

              <div className="space-y-4">
                <div className="relative">
                  <textarea
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder="Ex: Um assistente que ajuda a redigir e-mails profissionais, corrigir textos e sugerir melhorias na comunicação corporativa..."
                    rows={5}
                    className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-lg"
                    disabled={isGenerating}
                  />
                </div>

                <button
                  onClick={handleGenerateWithAI}
                  disabled={isGenerating || !aiPrompt.trim()}
                  className="w-full py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-medium text-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Gerando seu agente...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-5 h-5" />
                      Gerar Agente com IA
                    </>
                  )}
                </button>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
                  <p className="col-span-full text-sm text-gray-500 mb-2">💡 Exemplos de descrições:</p>
                  {[
                    'Assistente para criar posts no LinkedIn',
                    'Especialista em análise de contratos',
                    'Ajudante para organizar tarefas diárias',
                    'Consultor de finanças pessoais'
                  ].map((example) => (
                    <button
                      key={example}
                      onClick={() => setAiPrompt(example)}
                      disabled={isGenerating}
                      className="text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-600 transition-colors disabled:opacity-50"
                    >
                      "{example}"
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : creationMode === 'templates' && !assistant ? (
          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Escolha como começar</h3>
                <p className="text-gray-600">Crie com IA, use um template ou comece do zero</p>
              </div>

              {/* Opções principais */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {/* Criar com IA */}
                <button
                  onClick={() => setCreationMode('ai')}
                  className="p-6 border-2 border-purple-200 rounded-xl hover:border-purple-400 hover:bg-purple-50 transition-all group text-left"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <Wand2 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                        Criar com IA
                        <span className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded-full font-medium">
                          Recomendado
                        </span>
                      </h4>
                      <p className="text-sm text-gray-600">
                        Descreva o que precisa e a IA cria automaticamente
                      </p>
                    </div>
                  </div>
                </button>

                {/* Criar do Zero */}
                <button
                  onClick={() => setCreationMode('manual')}
                  className="p-6 border-2 border-dashed border-gray-200 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all group text-left"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition-colors">
                      <Sparkles className="w-6 h-6 text-gray-500 group-hover:text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Criar do Zero</h4>
                      <p className="text-sm text-gray-600">
                        Configure manualmente todos os detalhes
                      </p>
                    </div>
                  </div>
                </button>
              </div>

              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Templates Populares</h4>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {assistantTemplates.map(tmpl => (
                  <button
                    key={tmpl.id}
                    onClick={() => handleTemplateSelect(tmpl)}
                    className="text-left border border-gray-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-md transition-all group"
                  >
                    <div className="flex items-start gap-3">
                      <div className="text-3xl group-hover:scale-110 transition-transform">
                        {tmpl.avatar}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900 truncate">{tmpl.name}</h3>
                          {tmpl.isPopular && (
                            <span className="bg-yellow-100 text-yellow-700 text-xs px-2 py-0.5 rounded-full font-medium">
                              Popular
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2">{tmpl.description}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-gray-400">
                            {ASSISTANT_CATEGORIES.find(c => c.id === tmpl.category)?.icon}{' '}
                            {ASSISTANT_CATEGORIES.find(c => c.id === tmpl.category)?.name}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="flex border-b bg-gray-50/50">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  disabled={!assistant && tab.id !== 'config'}
                  className={`flex items-center gap-2 px-6 py-3 font-medium text-sm transition-all border-b-2 -mb-px ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600 bg-white'
                      : !assistant && tab.id !== 'config'
                      ? 'border-transparent text-gray-300 cursor-not-allowed'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {activeTab === 'config' && (
                <div className="p-6 max-w-3xl mx-auto space-y-6">
                  {/* Informações Básicas */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">1</span>
                      Informações Básicas
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="md:col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Nome do Agente *
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
                          placeholder="Ex: Agente de E-mails"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Avatar
                        </label>
                        <input
                          type="text"
                          value={formData.avatar}
                          onChange={(e) => setFormData(prev => ({ ...prev, avatar: e.target.value }))}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-2xl"
                          placeholder="🤖"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Descrição *
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        rows={2}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        placeholder="Descreva brevemente o que este agente faz..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Categoria
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {ASSISTANT_CATEGORIES.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.icon} {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Instruções */}
                  <div className="space-y-4 pt-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">2</span>
                      Instruções do Sistema
                    </h3>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Prompt do Sistema *
                      </label>
                      <textarea
                        value={formData.instructions}
                        onChange={(e) => setFormData(prev => ({ ...prev, instructions: e.target.value }))}
                        rows={8}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm"
                        placeholder="Defina como o agente deve se comportar, suas especialidades e estilo de resposta..."
                      />
                      <p className="text-xs text-gray-500 mt-1.5">
                        Estas instruções definem a personalidade e comportamento do agente.
                      </p>
                    </div>
                  </div>

                  {/* Configurações de IA */}
                  <div className="space-y-4 pt-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">3</span>
                      Configurações de IA
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Provedor de IA
                        </label>
                        <select
                          value={formData.aiProvider}
                          onChange={(e) => handleProviderChange(e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          {availableProviders.map(provider => (
                            <option key={provider.id} value={provider.id}>
                              {provider.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Modelo
                        </label>
                        <select
                          value={formData.modelId}
                          onChange={(e) => setFormData(prev => ({ ...prev, modelId: e.target.value }))}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          disabled={!currentProvider || currentProvider.models.length === 0}
                        >
                          {currentProvider?.models.map(model => (
                            <option key={model.id} value={model.id}>
                              {model.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="space-y-4 pt-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">4</span>
                      Tags
                    </h3>
                    
                    <div>
                      <div className="flex gap-2 mb-3">
                        <input
                          type="text"
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                          className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Adicionar tag..."
                        />
                        <button
                          type="button"
                          onClick={handleAddTag}
                          className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                        >
                          Adicionar
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {formData.tags.map(tag => (
                          <span
                            key={tag}
                            className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-blue-100 text-blue-800"
                          >
                            {tag}
                            <button
                              type="button"
                              onClick={() => handleRemoveTag(tag)}
                              className="ml-2 text-blue-600 hover:text-blue-800"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                        {formData.tags.length === 0 && (
                          <span className="text-sm text-gray-400">Nenhuma tag adicionada</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'tools' && assistant && (
                <div className="p-6">
                  <AssistantToolsConfig
                    assistantId={assistant.id}
                    userId={user.id}
                    tools={formData.tools}
                    onToolsChange={(tools) => setFormData(prev => ({ ...prev, tools }))}
                  />
                </div>
              )}

              {activeTab === 'knowledge' && assistant && (
                <div className="p-6">
                  <AssistantKnowledgeBase
                    assistantId={assistant.id}
                    userId={user.id}
                    tenantId={tenant.id}
                  />
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
              <div>
                {assistant && (
                  <button
                    type="button"
                    onClick={() => setShowDeleteModal(true)}
                    className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Excluir
                  </button>
                )}
              </div>
              
              <div className="flex items-center gap-3">
                {!assistant && creationMode === 'manual' && (
                  <button
                    type="button"
                    onClick={() => setCreationMode('templates')}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    ← Voltar
                  </button>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 font-medium"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    assistant ? 'Salvar Alterações' : 'Criar Agente'
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Excluir Agente</h3>
                <p className="text-sm text-gray-600">Esta ação não pode ser desfeita</p>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-gray-700">
                Tem certeza que deseja excluir o agente <strong>{assistant?.name}</strong>?
              </p>
              <p className="text-gray-600 text-sm mt-2">
                Todas as conversas e mensagens associadas também serão excluídas.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Excluindo...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Excluir
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

